import { NO_SCROLL_CLASS_NAME } from '../classnames/design-system';
import { NAVIGATION_FIXED } from '../classnames/navigation';
import {
  ACTIVE_SIDE_BAR,
  SIDE_MENU_CONTAINER,
  SIDE_MENU_CONTENT,
  SIDE_MENU_OVERLAY
} from '../classnames/sidebar-popup';
import ThirdPartyLinksHandler from '../shared/third-party-links-handler';
import {
  openOverlay,
  restoreBackground,
  revealOverlay,
  sharedFocusRestorer,
  trapTabFocus
} from '../shared/focus';
import { isEscapeKey, isTabKey } from '../shared/keyboard';
import { ACTIVE_POPUP_Z_INDEX, DEFAULT_Z_INDEX, DRAWER_SETTLE_MS } from './constants';

export class SideMenu {
  constructor() {
    this.thirdPartyLinksHandler = new ThirdPartyLinksHandler();
    // TODO - It shouldn't be here. It's navigation/side-menu.
    this.fixedNavBars = document.querySelectorAll(`.${NAVIGATION_FIXED}`);
  }

  show = (id) => {
    const element = document.getElementById(id);

    if (element.classList.contains(ACTIVE_SIDE_BAR)) {
      return;
    }

    sharedFocusRestorer.remember(element);

    element.classList.add(ACTIVE_SIDE_BAR);
    element.removeAttribute('inert');
    element.querySelector(SIDE_MENU_CONTAINER)?.scrollTo(0, 0);

    openOverlay(element);
    document.documentElement.classList.add(NO_SCROLL_CLASS_NAME);
    document.body.classList.add(NO_SCROLL_CLASS_NAME);
    this.thirdPartyLinksHandler.hideForSideBar();
    this.setNavBarsZIndex(ACTIVE_POPUP_Z_INDEX);
    this.moveFocusInside(element);
  };

  hide = (id) => {
    const element = document.getElementById(id);

    if (!element.classList.contains(ACTIVE_SIDE_BAR)) {
      return;
    }

    this.deactivate(element);
    this.restorePageState();
    sharedFocusRestorer.restore();
  };

  hideAll = () => {
    const activeMenus = document.querySelectorAll(`.${ACTIVE_SIDE_BAR}`);

    if (!activeMenus.length) {
      return;
    }

    activeMenus.forEach(this.deactivate);
    this.restorePageState();
    activeMenus.forEach(() => sharedFocusRestorer.restore());
  };

  deactivate = (element) => {
    restoreBackground(element);
    element.classList.remove(ACTIVE_SIDE_BAR);
    element.setAttribute('inert', '');
  };

  restorePageState = () => {
    document.documentElement.classList.remove(NO_SCROLL_CLASS_NAME);
    document.body.classList.remove(NO_SCROLL_CLASS_NAME);
    this.thirdPartyLinksHandler.showForSideBar();
    this.setNavBarsZIndex(DEFAULT_Z_INDEX);
  };

  setNavBarsZIndex = (zIndex) => {
    this.fixedNavBars.forEach((navBar) => {
      navBar.style.zIndex = zIndex;
    });
  };

  moveFocusInside = (element) => {
    const content = element.querySelector(`.${SIDE_MENU_CONTENT}`);
    const settling = new AbortController();

    const settle = () => {
      settling.abort();

      requestAnimationFrame(() => {
        if (sharedFocusRestorer.isTopmost(element)) {
          revealOverlay(element);
        }
      });
    };

    const settleTimer = setTimeout(settle, DRAWER_SETTLE_MS);

    settling.signal.addEventListener('abort', () => clearTimeout(settleTimer));
    content.addEventListener('transitionend', (event) => {
      if (event.target === content && event.propertyName === 'transform') {
        settle();
      }
    }, { signal: settling.signal });
  };

  handleKeydown = (event) => {
    const activeMenu = document.querySelector(`.${ACTIVE_SIDE_BAR}`);

    if (event.defaultPrevented || !sharedFocusRestorer.isTopmost(activeMenu)) {
      return;
    }

    if (isEscapeKey(event)) {
      this.hideAll();
    } else if (isTabKey(event)) {
      trapTabFocus(event, activeMenu);
    }
  };

  initializeEventListeners = () => {
    document.addEventListener('keydown', this.handleKeydown);

    document.querySelectorAll(`.${SIDE_MENU_OVERLAY}`).forEach((overlay) => {
      overlay.addEventListener('click', this.hideAll);
    });
  };
}
